/**
 * Mutation helper: runs a typed call, invalidates cached reads, and toasts the outcome with the
 * audit event id (`Recorded · evt 8f3a…`). Errors toast with the request id; recent-auth failures
 * offer Re-authenticate.
 */
import { useToast } from '@agency/ui';
import { useCallback, useState } from 'react';
import { ApiError, call, shortId, type CallArgs, type RouteId, type RouteOutput } from './api.js';
import { useSession } from './session.js';
import { invalidate } from './useApi.js';

export interface MutateOptions<Id extends RouteId> {
  /** Toast title on success. */
  title?: string | ((data: RouteOutput<Id>) => string);
  /** Route id prefixes whose cached reads must reload. */
  invalidate?: string[];
  /** Suppress the success toast. */
  silent?: boolean;
}

export interface Mutator {
  run: <Id extends RouteId>(id: Id, args?: CallArgs<Id>, options?: MutateOptions<Id>) => Promise<RouteOutput<Id> | null>;
  busy: boolean;
  error: ApiError | null;
  clearError: () => void;
}

export function describeError(error: ApiError): string {
  const base = error.message || error.code;
  return error.requestId ? `${base} · request ${shortId(error.requestId)}` : base;
}

export function useMutation(): Mutator {
  const { notify } = useToast();
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const run = useCallback(
    async <Id extends RouteId>(id: Id, args: CallArgs<Id> = {}, options: MutateOptions<Id> = {}): Promise<RouteOutput<Id> | null> => {
      setBusy(true);
      setError(null);
      try {
        const res = await call(id, args);
        if (options.invalidate?.length) invalidate(...options.invalidate);
        if (!options.silent) {
          const title = typeof options.title === 'function' ? options.title(res.data) : (options.title ?? 'Saved');
          notify({
            title,
            description: res.auditEventId ? `Recorded · evt ${shortId(res.auditEventId)}` : undefined,
            tone: 'ok',
          });
        }
        return res.data;
      } catch (e) {
        const err = ApiError.from(e);
        setError(err);
        if (err.code === 'recent_auth_required') {
          notify({ title: 'Recent authentication required', description: describeError(err), tone: 'warn', duration: 0, action: { label: 'Re-authenticate', onSelect: () => session.reauthenticate() } });
        } else if (err.status !== 401) {
          notify({ title: 'Request failed', description: describeError(err), tone: 'brand', duration: 0 });
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [notify, session],
  );

  return { run, busy, error, clearError: () => setError(null) };
}
